import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';
import { isSelfPayOfferType } from '@phx/data-sources/medication-marketplace';

import { useFillPrescription } from '../../../../contexts/fill/use-fill-prescription';
import { CoverageCardType } from '../../../../graphql/generated/graphql';
import { useProgramTerms } from '../../../../hooks/use-program-terms';
import { getAbsoluteRoute } from '../../../../util/get-absolute-route';
import { isPharmacySpecificOffer } from '../../../../util/offers/is-pharmacy-specific-offer';
import { AttestationContent } from '../common/AttestationContent';
import { ForwardToPharmacySelection } from '../common/ForwardToPharmacySelection';

import { DeclineSelfPayAssistanceModal } from './DeclineSelfPayAssistanceModal';

export const SelfPayAttestation = () => {
    const [showDeclineModal, { toggle, close }] = useDisclosure(false);
    const { t } = useTranslation('lilly');
    const navigate = useNavigate();

    const { drxId, medication, allOffers } = useFillPrescription();
    const terms = useProgramTerms(medication, 'self-pay');

    const selfPayOffers = allOffers.filter((o) =>
        isSelfPayOfferType(o.headlinePriceType)
    );

    const declineCallback = useCallback(() => {
        if (selfPayOffers.some(isPharmacySpecificOffer)) {
            navigate({
                pathname: getAbsoluteRoute('fill.drxId.pharmacy.root', {
                    drxId,
                }),
                search: `?pathway=fallback&original-pathway=self-pay`,
            });
        }
    }, [drxId, navigate, selfPayOffers]);

    const declineHandler = !selfPayOffers.some(isPharmacySpecificOffer)
        ? toggle
        : undefined;

    // No T&C means there isn't a cash assistance card, but there may still be a self-pay flow
    if (!terms || !terms?.body) {
        return <ForwardToPharmacySelection drxId={drxId} />;
    }

    return (
        <AttestationContent
            terms={terms}
            acceptLabel={t('fill.selfPay.attestation.accept')}
            cardType={CoverageCardType.CashCard}
            declineAction={declineHandler}
            declineCallback={declineCallback}
            declineLabel={t('fill.selfPay.attestation.decline')}
        >
            <DeclineSelfPayAssistanceModal
                isOpen={showDeclineModal}
                close={close}
            />
        </AttestationContent>
    );
};
